export interface Error {
  category?: string;
  code?: string;
  message: string;
  offendingFields?: any;
}

export interface EffectError {
  error: FeeLookupError;
}

export interface AmountDetails {
  value: number;
  currencyCode: string;
}

export interface SettlementDetails {
  reconcileReceiveAmount: AmountDetails;
  reconcileSendAmount: AmountDetails;
  walletAddress: string;
  walletMemo: string | number;
}

export interface ConfirmRefundResponse {
  expectedPayoutDate: string;
  referenceNumber: string | number;
  settlementDetails: SettlementDetails;
  transactionId: string | number;
}

export interface RefundResponse {
  dateTimeSent?: string;
  deliveryOptionId?: string;
  fundStatus?: string;
  isRefundAllowedFlag?: boolean;
  refundFeeAmount?: AmountDetails;
  refundSendAmount?: AmountDetails;
  refundTotalAmount: AmountDetails;
  sendAmount?: AmountDetails;
  transactionDate?: string;
  transactionStatus?: string;
  errors?: Error[];
}

export interface TransactionState {
  destinationCountryData?: DestinationCountryData;
  feeLookupResponse?: Transaction; // TODO: Should this be using the feeLookupResponse Interface instead of Transaction
  sendValidateRequest?: SendValidateRequest;
  sendValidateResponse: SendValidateResponse;
  sendCommitResponse?: any;
  transactionStatusResponse?: any;
  getRefundResponse?: RefundResponse;
  confirmRefundResponse?: any;
  selectedLocation?: any;
  paymentMethod?: PaymentMethod;
}

export interface ConfirmRefundRequest {
  userLanguage: string;
  targetAudience: string;
  refundReasonCode: string;
  sendAmount: AmountDetails;
}

export interface SendValidateRequest {
  targetAudience?: string;
  userLanguage?: string;
  destinationCountryCode?: string;
  destinationCountrySubdivisionCode?: string;
  payoutOptionCode?: string;
  payoutAmount?: AmountDetails;
  sendAmount?: AmountDetails;
  payoutCurrencyCode?: string;
  stagingInfo?: {
    stageTransaction?: boolean;
    sendLocationAgentPartnerId?: string;
    formFreeCompletion?: boolean;
  };
  sender?: SenderRegistrationInfo;
  additionalTransactionDetails?: {
    relationshipToReceiver?: string;
    purposeOfTransactionCode?: string;
    sourceOfFundsCode?: string;
    proofOfFundsCode?: string;
    intendedUseOfMGIServicesCode?: string;
    getRelationshipToReceiver?: boolean;
  };
  receiverSameAsSender?: boolean;
}

export interface SenderRegistrationInfo {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  secondLastName?: string;
  email?: string;
  notificationLanguagePreference?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    countrySubdivisionCode?: string;
    countryCode?: string;
    postalCode?: string | number;
  };
  dateOfBirth?: string;
  mobilePhone?: {
    number?: string;
    countryDialCode?: string;
  };
  birthCountryCode?: string;
  primaryIdentification?: {
    typeCode?: string;
    id?: string;
    issueCountrySubdivisionCode?: string;
    issueCountryCode?: string;
  };
  secondaryIdentification?: {
    typeCode?: string;
    id?: string;
    issueCountrySubdivisionCode?: string;
    issueCountryCode?: string;
  };
  politicalExposedPerson?: boolean;
}

export interface SendValidateResponse {
  readyForCommit?: boolean;
  transactionId?: string;
  sendAmountDetails: SendAmountDetails;
  payoutAmountDetails: PayoutAmountDetails;
  errors?: Error[];
}

export interface DestinationCountryData {
  amount?: string;
  country?: string;
  state?: string;
  currency?: string;
}

export interface KeyValue {
  key: string;
  value: any;
}
export interface FeeLookUpRequest {
  targetAudience?: string;
  userLanguage?: string;
  destinationCountryCode?: string;
  destinationCountrySubdivisionCode?: string;
  payoutOptionCode?: string;
  payoutAmount?: AmountDetails;
  sendAmount?: AmountDetails;
  stagingInfo?: {
    stageTransaction: true;
    sendLocationAgentPartnerId: string;
    formFreeCompletion: boolean;
  };
  payoutCurrencyCode?: string;
  sendCurrencyCode?: string;
  customFields?: KeyValue[];
}

export interface FeeLookupResponse {
  transactions?: Transaction[];
  errors?: Error[];
  senderProfile?: SenderRegistrationInfo;
  additionalInfos?: AdditionalInfos[];
}

export interface FeeLookupError {
  additionalInfos?: KeyValue[];
  errors: Error[];
  transactions?: { transactionId: string }[];
}

export interface SendAmountDetails {
  amount?: AmountDetails;
  fees: AmountDetails;
  taxes?: AmountDetails;
  partnerFees?: AmountDetails;
  partnerTaxes?: AmountDetails;
  totalAmount?: AmountDetails;
  discounts?: AmountDetails;
  fxRate: any;
}

export interface PayoutAmountDetails {
  amount: AmountDetails;
  fees?: AmountDetails;
  taxes?: AmountDetails;
  partnerFees?: AmountDetails;
  partnerTaxes?: AmountDetails;
  totalAmount?: AmountDetails;
  fxRate: null;
  fxRateEstimated: null;
}

export interface PayoutOption {
  code: string;
  categoryCode: string;
  payoutPartnerId: null;
  estimatedDeliveryDate: null;
}
export interface Transaction {
  transactionId?: string;
  sendAmountDetails?: SendAmountDetails;
  payoutAmountDetails?: PayoutAmountDetails;
  payoutOption?: PayoutOption;
  cryptoNetworkSettlementDetails?: null;
  destinationCountryData?: DestinationCountryData;
  feeLookupResponse?: FeeLookupResponse;
  additionalInfos?: AdditionalInfos[];
}

export interface AdditionalInfos {
  key?: string;
  value?: string;
}

export interface ConfirmResponse {
  transactionId: string;
  transactionCryptoNetworkInstructions: {
    accountAddress: string;
    operationMemo: string;
  };
  sep24TransactionResponse: {
    withdraw_anchor_account: string;
    withdraw_memo: string;
    withdraw_memo_type: string;
    id: string;
    kind: string;
    status: string;
    more_info_url: string;
    amount_in: string;
    amount_in_asset: string;
    amount_out: string;
    amount_out_asset: string;
    amount_fee: string;
    amount_fee_asset: string;
    started_at: string;
    stellar_transaction_id: string;
    external_transaction_id: string;
    refunded: boolean;
    from: string;
    to: string;
  };
  referenceNumber?: string;
  confirmationCode?: string;
}


export type PaymentMethod = 'cash' | 'debit' | 'bank' | null;